import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAppState } from '@state/AppStateProvider';
import { useLogger } from '@providers/loggerProvider';
import { useUserSetsService } from '@hooks/services/useUserSetsService';

const EMPTY_ARRAY = [];

/**
 * Action handler for PickListCompletionDialog. Global (app-scoped) state so
 * either the Pick List page or the User Set Details pick list can open it
 * from the same `markPickListItemPicked` response field, `set_completion`.
 *
 * @returns {Object} Event handlers and data for the component
 */
export const usePickListCompletionDialogActionHandler = () => {
    const logger = useLogger('PickListCompletionDialogActionHandler');
    const { getState, setState } = useAppState();
    const userSetsService = useUserSetsService();

    const itemType = getState({ context: 'app', path: 'pickListCompletionDialog.itemType' });
    const name = getState({ context: 'app', path: 'pickListCompletionDialog.name' });
    const number = getState({ context: 'app', path: 'pickListCompletionDialog.number' });
    const trackedInUserSets = getState({ context: 'app', path: 'pickListCompletionDialog.trackedInUserSets' }) || EMPTY_ARRAY;

    const title = itemType === 'MINIFIG' ? 'Minifigure Completed' : 'Set Completed';
    const message = `${name} (${number}) is now complete.`;

    const handleClose = useCallback(() => {
        logger.debug('Closing PickListCompletionDialog', 'handleClose');
        setState({ context: 'app', path: 'pickListCompletionDialog.isOpen', value: false });
    }, [setState, logger]);

    // Applying to a tracked set is a single click, single copy action, so it
    // calls the service directly. The dialog closes immediately on click per
    // design; a failed apply toasts an error rather than reopening.
    const handleApply = useCallback(async (userSetId, userSetItemId) => {
        setState({ context: 'app', path: 'pickListCompletionDialog.isOpen', value: false });
        try {
            await userSetsService.allocateToSet(userSetId, userSetItemId, 1);
        } catch (error) {
            logger.error('Failed to apply completed minifigure to tracked set', 'handleApply', { error, userSetId, userSetItemId });
            toast.error('Failed to apply the minifigure to the tracked set.');
            throw error;
        }
    }, [setState, userSetsService, logger]);

    const openFromCompletion = useCallback((completion) => {
        setState({ context: 'app', path: 'pickListCompletionDialog.itemType', value: completion.item_type });
        setState({ context: 'app', path: 'pickListCompletionDialog.name', value: completion.name });
        setState({ context: 'app', path: 'pickListCompletionDialog.number', value: completion.number });
        setState({
            context: 'app',
            path: 'pickListCompletionDialog.trackedInUserSets',
            value: completion.tracked_in_user_sets || EMPTY_ARRAY
        });
        setState({ context: 'app', path: 'pickListCompletionDialog.isOpen', value: true });
    }, [setState]);

    return {
        handleClose,
        handleApply,
        openFromCompletion,
        title,
        message,
        trackedInUserSets
    };
};

export default usePickListCompletionDialogActionHandler;
